'use client';

import { cdn } from '@/lib/cdn';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { FreeMode, Keyboard, Mousewheel } from 'swiper/modules';
import { Swiper, SwiperSlide, type SwiperRef } from 'swiper/react';

/**
 * Simple throttle implementation
 * TODO: Replace with lodash/throttle if lodash is added as a dependency
 */
function _throttle<T extends (...args: Parameters<T>) => ReturnType<T>>(func: T, limit: number): T {
  let inThrottle: boolean;
  let lastResult: ReturnType<T>;

  return ((...args: Parameters<T>) => {
    if (!inThrottle) {
      lastResult = func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
    return lastResult;
  }) as T;
}

import { useAriaLabels } from '@/hooks/useAriaLabels';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useHeaderOffset } from './hooks/UseHeaderOffset';
import { containsPattern } from './utils/ContainsPattern';

// Sub-components
import { NavQuickLinkBannerItem } from './components/NavQuickLinkBannerItem';
import { NavQuickLinkExhibitionsItem } from './components/NavQuickLinkExhibitionsItem';
import { NavQuickLinkItem, type NavQuickLinkMedia } from './components/NavQuickLinkItem';
import { NavQuickLinkPillItem } from './components/NavQuickLinkPillItem';
import { NavQuickLinkStoriesItem } from './components/NavQuickLinkStoriesItem';
import { NavQuickLinksVertical } from './components/NavQuickLinksVertical';

// Already ported component
import { HistoryNavigations } from '@/components/cms/HistoryNavigations';

import type { UrlTriggerEvent } from './hooks/UseNavigation';

import styles from './NavQuickLinks.module.scss';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/free-mode';

// =============================================================================
// TYPES
// =============================================================================

/**
 * NavQuickLinks display type
 * Determines which layout/variant to render
 */
export type NavQuickLinksType =
  | 'Banner' // Text links on dark background
  | 'Exhibition' // Timeline carousel
  | 'Stories' // Story card carousel
  | 'Vertical Links' // Sidebar stepper
  | 'HistoryBottomNavigation' // Prev/next navigation (uses HistoryNavigations)
  | 'Pill' // Pill-shaped tabs
  | 'Rectangle' // Rectangular tabs (pill behaviour, square corners)
  | 'Bubble' // Circular icon buttons
  | 'Text'; // Text links with accordion on mobile

/**
 * Individual NavQuickLink item from Contentful
 */
export interface NavQuickLinkFragment {
  __typename?: 'NavQuickLink';
  sys: { id: string };
  backgroundColor?: string | null;
  disableNavigationScrollToTop?: boolean | null;
  icon?: string | null;
  media?: NavQuickLinkMedia | null;
  topLabelText?: string | null;
  url?: string | null;
  urlTarget?: string | null;
  urlText?: string | null;
  urlTrigger?: UrlTriggerEvent | null;
}

/**
 * NavQuickLinks props (matches Contentful fragment)
 */
export interface NavQuickLinksProps {
  __typename?: 'NavQuickLinks';
  sys: { id: string };
  /** Whether to stick to top of viewport */
  isSticky?: boolean | null;
  /** Collection of link items */
  linksCollection?: {
    items: Array<NavQuickLinkFragment | null>;
  } | null;
  /** Background color for the wrapper */
  navQuickLinkBackgroundColor?: string | null;
  /** Display type */
  type?: NavQuickLinksType | string | null;
  /** CMS index for unique data-testid generation */
  cmsIndex?: number;
  /** Optional className */
  className?: string;
}

// =============================================================================
// ACCORDION COMPONENT (Mobile Text type)
// =============================================================================

/**
 * =============================================================================
 * ACCORDION COMPONENT - STUB
 * =============================================================================
 *
 * This is a simplified accordion for mobile "Text" type.
 *
 * TODO: Replace with proper Accordion component when ported
 * Original: src/components/Accordion
 *
 * The accordion shows the current selection as the header and expands
 * to show all options when clicked.
 */
interface AccordionProps {
  isOpen: boolean;
  onToggle: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

function SimpleAccordion({ isOpen, onToggle, title, children, className }: AccordionProps) {
  return (
    <div className={clsx(styles.accordionWrapper, className)}>
      <button
        type="button"
        onClick={onToggle}
        className={styles.accordionHeader}
        aria-expanded={isOpen}
      >
        {title}
      </button>
      {isOpen && <div className={styles.accordionContent}>{children}</div>}
    </div>
  );
}

// =============================================================================
// TYPE-SPECIFIC RENDER COMPONENTS
// =============================================================================

function useIsLinkActive() {
  const pathname = usePathname();

  return (linkUrl?: string | null) => {
    if (!linkUrl || !pathname) return false;
    try {
      const urlRegex = new RegExp(linkUrl);
      return urlRegex.test(pathname);
    } catch {
      return pathname.includes(linkUrl);
    }
  };
}

type TabNavProps = {
  linksCollection: NavQuickLinksProps['linksCollection'];
  cmsIndex?: number;
  shape: 'pill' | 'rectangle';
  categoryClassName?: string;
  wrapperTestId: string;
  itemTestIdPrefix: string;
};

/**
 * Pill and Rectangle types share the same tab behaviour — only the chip shape differs.
 */
function TabNav({
  linksCollection,
  shape,
  categoryClassName,
  wrapperTestId,
  itemTestIdPrefix,
}: Readonly<TabNavProps>) {
  const isLinkActive = useIsLinkActive();

  if (!linksCollection?.items.length) {
    return <div data-testid="emptyBox" />;
  }

  return (
    <div className={clsx(categoryClassName ?? styles.categoryWrapper)} data-testid={wrapperTestId}>
      {linksCollection.items.map((item, index) => {
        if (!item) return null;
        return (
          <NavQuickLinkPillItem
            key={item.sys.id}
            {...item}
            shape={shape}
            isCurrentLink={isLinkActive(item.url)}
            data-testid={`${itemTestIdPrefix}-${index}`}
          />
        );
      })}
    </div>
  );
}

function PillNav({
  linksCollection,
  cmsIndex: _cmsIndex,
}: Readonly<{
  linksCollection: NavQuickLinksProps['linksCollection'];
  cmsIndex?: number;
}>) {
  return (
    <TabNav
      linksCollection={linksCollection}
      shape="pill"
      wrapperTestId="PillCategoryWrapper"
      itemTestIdPrefix="PillNavQuickLinkPillItem"
    />
  );
}

function RectangleNav({
  linksCollection,
  cmsIndex: _cmsIndex,
}: Readonly<{
  linksCollection: NavQuickLinksProps['linksCollection'];
  cmsIndex?: number;
}>) {
  return (
    <TabNav
      linksCollection={linksCollection}
      shape="rectangle"
      categoryClassName={styles.rectangleCategoryWrapper}
      wrapperTestId="RectangleCategoryWrapper"
      itemTestIdPrefix="RectangleNavQuickLinkItem"
    />
  );
}

/**
 * Bubble type - Circular icon/image buttons
 */
function BubbleNav({
  linksCollection,
  cmsIndex: _cmsIndex,
}: {
  linksCollection: NavQuickLinksProps['linksCollection'];
  cmsIndex?: number;
}) {
  if (!linksCollection?.items.length) {
    return <div data-testid="emptyBox" />;
  }

  return (
    <div className={styles.categoryWrapper} data-testid="BubbleCategoryWrapper">
      {linksCollection.items.map((item, index) => {
        if (!item) return null;
        return (
          <NavQuickLinkItem
            key={item.sys.id}
            {...item}
            data-testid={`BubbleNavQuickLinkItem-${index}`}
          />
        );
      })}
    </div>
  );
}

/**
 * Banner type - Text links on dark background
 */
function BannerNav({
  linksCollection,
  cmsIndex: _cmsIndex,
}: {
  linksCollection: NavQuickLinksProps['linksCollection'];
  cmsIndex?: number;
}) {
  if (!linksCollection?.items.length) {
    return <div data-testid="emptyBox" />;
  }

  return (
    <div className={styles.bannerCategoryWrapper} data-testid="BannerCategoryWrapper">
      {linksCollection.items.map((item, index) => {
        if (!item) return null;
        return (
          <NavQuickLinkBannerItem
            key={item.sys.id}
            {...item}
            data-testid={`BannerNavQuickBannerItem-${index}`}
          />
        );
      })}
    </div>
  );
}

/**
 * Text type - Text links with accordion on mobile
 */
function TextNav({
  linksCollection,
  type,
  cmsIndex: _cmsIndex,
}: {
  linksCollection: NavQuickLinksProps['linksCollection'];
  type?: string | null;
  cmsIndex?: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState<NavQuickLinkFragment | null>(null);
  const isMobile = useMediaQuery('(max-width: 639px)');

  // Initialize current item
  useEffect(() => {
    if (linksCollection?.items?.[0]) {
      setCurrentItem(linksCollection.items[0]);
    }
  }, [linksCollection]);

  if (!linksCollection?.items.length) {
    return <div data-testid="emptyBox" />;
  }

  const handleToggle = () => setIsOpen(!isOpen);

  const handleSelectItem = (item: NavQuickLinkFragment) => {
    setCurrentItem(item);
    setIsOpen(false);
    // Navigate to the URL
    if (item.url) {
      window.open(item.url, item.urlTarget ?? '_self');
    }
  };

  // Desktop: Show all items
  if (!isMobile) {
    return (
      <div className={styles.categoryWrapper} data-testid="SquareCategoryWrapper">
        {linksCollection.items.map((item, index) => {
          if (!item) return null;
          return (
            <NavQuickLinkItem
              key={item.sys.id}
              {...item}
              type={type}
              data-testid={`SquareNavQuickLinkItem-${index}`}
            />
          );
        })}
      </div>
    );
  }

  // Mobile: Accordion with current selection
  const isSvgImage = currentItem?.media?.contentType?.includes('svg') ?? false;

  return (
    <SimpleAccordion
      isOpen={isOpen}
      onToggle={handleToggle}
      title={
        <div
          className={clsx(
            styles.accordionItem,
            isOpen && styles.open,
            isSvgImage && styles.svgImage
          )}
          data-testid="AccordionItem"
        >
          {currentItem?.media?.url && (
            <div className={styles.accordionBox}>
              <img
                src={cdn.transformImageUrl(currentItem.media.url, {
                  width: 48,
                  quality: 90,
                  format: 'auto',
                })}
                alt={currentItem.media.title ?? ''}
                width={24}
                height={24}
                aria-hidden
                data-testid="AccordionImage"
              />
            </div>
          )}
          {currentItem?.urlText}
        </div>
      }
    >
      {linksCollection.items.map((item) => {
        if (!item) return null;
        return (
          <button
            key={item.sys.id}
            className={styles.accordionItem}
            onClick={() => handleSelectItem(item)}
            type="button"
          >
            {item.media?.url && (
              <div className={styles.accordionBox}>
                <img
                  src={cdn.transformImageUrl(item.media.url, {
                    width: 48,
                    quality: 90,
                    format: 'auto',
                  })}
                  alt={item.media.title ?? ''}
                  width={24}
                  height={24}
                  aria-hidden
                />
              </div>
            )}
            {item.urlText}
          </button>
        );
      })}
    </SimpleAccordion>
  );
}

/**
 * Stories type - Story card Swiper carousel
 */
function StoriesNav({
  linksCollection,
  navQuickLinkBackgroundColor,
  cmsIndex: _cmsIndex,
}: {
  linksCollection: NavQuickLinksProps['linksCollection'];
  navQuickLinkBackgroundColor?: string | null;
  cmsIndex?: number;
}) {
  const swiperRef = useRef<SwiperRef>(null);
  const pathname = usePathname();
  const isMobile = useMediaQuery('(max-width: 767px)');

  const items = linksCollection?.items?.filter(Boolean) ?? [];

  // Find current index based on URL
  const currentIndex = items.findIndex((item) => {
    if (!item?.url) return false;
    const isContains = containsPattern(pathname, item.url);
    return item.url === pathname || isContains;
  });

  // Slide to current item on mount/change
  useEffect(() => {
    if (currentIndex >= 0 && swiperRef.current?.swiper) {
      swiperRef.current.swiper.slideTo(currentIndex);
    }
  }, [currentIndex]);

  // Calculate ruler width based on items count
  const getRulerWidth = () => {
    const linksLength = items.length;
    return isMobile ? linksLength * 170 : linksLength * 244;
  };

  if (!items.length) {
    return <div data-testid="emptyBox" />;
  }

  return (
    <div
      className={styles.storiesWithImageWrapper}
      style={navQuickLinkBackgroundColor ? { background: navQuickLinkBackgroundColor } : undefined}
    >
      <div className={styles.swiperBox}>
        <Swiper
          ref={swiperRef}
          className={styles.storiesSwiper}
          modules={[FreeMode]}
          freeMode={!isMobile}
          slidesPerView="auto"
          spaceBetween={16}
          centerInsufficientSlides
          data-testid="StoriesCategoryWrapper"
        >
          {items.map((item, index) => {
            if (!item) return null;
            return (
              <SwiperSlide key={item.sys.id}>
                {/* Ruler decoration on first item */}
                {index === 0 && (
                  <div className={styles.rulerImage} style={{ width: getRulerWidth() }} />
                )}
                <NavQuickLinkStoriesItem
                  {...item}
                  backgroundColor={navQuickLinkBackgroundColor}
                  isSwiperActive={index === currentIndex}
                  data-testid={`StoriesNavQuickLinkItem-${index}`}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </div>
  );
}

/**
 * Exhibitions type - Timeline Swiper carousel
 */
function ExhibitionsNav({
  linksCollection,
  navQuickLinkBackgroundColor,
  cmsIndex: _cmsIndex,
}: {
  linksCollection: NavQuickLinksProps['linksCollection'];
  navQuickLinkBackgroundColor?: string | null;
  cmsIndex?: number;
}) {
  const swiperRef = useRef<SwiperRef>(null);
  const pathname = usePathname();
  const isMobile = useMediaQuery('(max-width: 767px)');

  const items = linksCollection?.items?.filter(Boolean) ?? [];

  // Find current index based on URL
  const currentIndex = items.findIndex((item) => {
    if (!item?.url) return false;
    const isContains = containsPattern(pathname, item.url);
    return item.url === pathname || isContains;
  });

  // Slide to current item on mount/change
  useEffect(() => {
    if (currentIndex >= 0 && swiperRef.current?.swiper) {
      swiperRef.current.swiper.slideTo(currentIndex);
    }
  }, [currentIndex]);

  if (!items.length) {
    return <div data-testid="emptyBox" />;
  }

  return (
    <div
      className={styles.exhibitionsWithImageWrapper}
      style={navQuickLinkBackgroundColor ? { background: navQuickLinkBackgroundColor } : undefined}
    >
      <Swiper
        ref={swiperRef}
        className={styles.exhibitionsSwiper}
        modules={[Mousewheel, Keyboard, FreeMode]}
        freeMode
        mousewheel
        slidesPerView="auto"
        data-testid="ExhibitionsCategoryWrapper"
      >
        {items.map((item, index) => {
          if (!item) return null;
          return (
            <SwiperSlide key={item.sys.id}>
              {/* Ruler decoration on first item (desktop only) */}
              {index === 0 && !isMobile && <div className={styles.exhibitionsRulerImage} />}
              <NavQuickLinkExhibitionsItem
                {...item}
                backgroundColor={navQuickLinkBackgroundColor}
                isSwiperActive={index === currentIndex}
                data-testid={`ExhibitionsNavQuickLinkItem-${index}`}
              />
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
}

/**
 * Vertical Links type - Sidebar stepper navigation
 */
function VerticalLinksNav({
  linksCollection,
  cmsIndex: _cmsIndex,
}: {
  linksCollection: NavQuickLinksProps['linksCollection'];
  cmsIndex?: number;
}) {
  const [isSticky, setIsSticky] = useState(false);
  const [footerHeight, setFooterHeight] = useState(600);
  const [mainHeight, setMainHeight] = useState(0);
  const verticalLinksRef = useRef<HTMLDivElement>(null);

  // Get footer and main heights for positioning
  useEffect(() => {
    const footer = document.querySelector('footer');
    const main = document.querySelector('main');

    if (footer) {
      setFooterHeight(footer.offsetHeight);
    }
    if (main) {
      setMainHeight(main.offsetHeight);
    }
  }, []);

  // Set up scroll listener with throttled handler
  useEffect(() => {
    let inThrottle = false;

    const handleScroll = () => {
      if (inThrottle) return;
      inThrottle = true;

      setTimeout(() => {
        const element = verticalLinksRef.current;
        if (element) {
          const elementTopToDocumentTop = element.getBoundingClientRect().top;
          const shouldStick = elementTopToDocumentTop <= 0;
          setIsSticky(shouldStick);
        }
        inThrottle = false;
      }, 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!linksCollection?.items.length) {
    return <div data-testid="emptyBox" />;
  }

  return (
    <div
      ref={verticalLinksRef}
      className={clsx(styles.verticalWrapper, isSticky && styles.sticky)}
      style={{ minHeight: mainHeight - footerHeight - 240 }}
      data-testid="NavQuickLinksVertical"
    >
      <NavQuickLinksVertical linksCollection={linksCollection} sys={{ id: 'Vertical' }} />
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * NavQuickLinks - Polymorphic quick navigation component
 *
 * Renders different navigation layouts based on the `type` prop:
 * - Banner: Text links on dark background
 * - Pill: Pill-shaped tabs with active state
 * - Rectangle: Rectangular tabs with the same behaviour as Pill
 * - Bubble: Circular icon/image buttons
 * - Text: Text links with accordion on mobile
 * - Stories: Story card Swiper carousel
 * - Exhibition: Timeline Swiper carousel
 * - Vertical Links: Sidebar stepper navigation
 * - HistoryBottomNavigation: Uses HistoryNavigations component
 *
 * Features:
 * - Sticky positioning support
 * - Background color customization
 * - Active state detection based on current URL
 * - Responsive layouts
 * - CMS index for data-testid generation
 *
 * @example
 * <NavQuickLinks
 *   type="Pill"
 *   linksCollection={linksData}
 *   isSticky
 *   cmsIndex={0}
 * />
 */
export function NavQuickLinks({
  isSticky,
  linksCollection,
  navQuickLinkBackgroundColor,
  sys,
  type,
  cmsIndex,
  className,
}: Readonly<NavQuickLinksProps>) {
  const aria = useAriaLabels();
  const headerOffset = useHeaderOffset();

  const testId = cmsIndex !== undefined ? `navquicklinks-${cmsIndex}` : undefined;

  // Type-specific rendering
  switch (type) {
    case 'Banner':
      return (
        <nav data-testid={testId ?? 'NavQuickLinksBanner'} className={className}>
          <BannerNav linksCollection={linksCollection} cmsIndex={cmsIndex} />
        </nav>
      );

    case 'Exhibition':
      return (
        <nav data-testid={testId ?? 'NavQuickLinksExhibitions'} className={className}>
          <ExhibitionsNav
            linksCollection={linksCollection}
            navQuickLinkBackgroundColor={navQuickLinkBackgroundColor}
            cmsIndex={cmsIndex}
          />
        </nav>
      );

    case 'Stories':
      return (
        <nav data-testid={testId ?? 'NavQuickLinksStories'} className={className}>
          <StoriesNav
            linksCollection={linksCollection}
            navQuickLinkBackgroundColor={navQuickLinkBackgroundColor}
            cmsIndex={cmsIndex}
          />
        </nav>
      );

    case 'Vertical Links':
      return (
        <nav className={className}>
          <VerticalLinksNav linksCollection={linksCollection} cmsIndex={cmsIndex} />
        </nav>
      );

    case 'HistoryBottomNavigation':
      // Use the already-ported HistoryNavigations component
      return <HistoryNavigations linksCollection={linksCollection as never} />;

    // Default: Pill, Rectangle, Bubble, Text types use the wrapper layout
    default: {
      const isRectangle = type === 'Rectangle';
      const defaultRectangleBackground = isRectangle
        ? 'var(--color-brand-background-grey)'
        : undefined;

      return (
        <nav
          key={sys?.id}
          className={clsx(
            styles.innerWrapper,
            isRectangle && styles.rectangleInnerWrapper,
            isSticky && styles.sticky,
            className
          )}
          style={{
            ...((navQuickLinkBackgroundColor ?? defaultRectangleBackground)
              ? { background: navQuickLinkBackgroundColor ?? defaultRectangleBackground }
              : {}),
            ...(isSticky ? { top: headerOffset } : {}),
          }}
          aria-label={aria.action('pageSectionsNavigation')}
          data-testid={testId ?? 'NavQuickLinksInnerWrapper'}
        >
          <div
            className={clsx(styles.headerBox, isRectangle && styles.rectangleHeaderBox)}
            data-testid="NavQuickLinksHeaderBox"
          >
            {type === 'Pill' && (
              <div className={styles.flexBox} data-testid="NavQuickLinksFlex">
                <PillNav linksCollection={linksCollection} cmsIndex={cmsIndex} />
              </div>
            )}

            {type === 'Rectangle' && (
              <div
                className={clsx(styles.flexBox, styles.rectangleFlexBox)}
                data-testid="NavQuickLinksFlex"
              >
                <RectangleNav linksCollection={linksCollection} cmsIndex={cmsIndex} />
              </div>
            )}

            {type === 'Bubble' && (
              <div className={styles.flexBox} data-testid="NavQuickLinksFlex">
                <BubbleNav linksCollection={linksCollection} cmsIndex={cmsIndex} />
              </div>
            )}

            {type === 'Text' && (
              <div className={styles.textFlexBox} data-testid="NavQuickLinksFlex">
                <TextNav linksCollection={linksCollection} type={type} cmsIndex={cmsIndex} />
              </div>
            )}
          </div>
        </nav>
      );
    }
  }
}

NavQuickLinks.displayName = 'NavQuickLinks';
